import { createContext, ReactNode, useCallback, useContext } from 'react'

import {
  Board,
  BoardContextType,
  DefaultCardType,
  InvestimentCardType,
} from '../type'
import { noop } from '../utils'

const BoardContext = createContext<BoardContextType>({
  data: [],
  moveCard: noop,
})

interface IBoardProviderProps {
  data: Board
  setData: React.Dispatch<React.SetStateAction<Board>>
  children: ReactNode
  onDropCard?: (..._param: any) => void
}

const BoardProvider = ({
  data,
  setData,
  children,
  onDropCard,
}: IBoardProviderProps) => {
  const moveCard = useCallback(
    (cardId: string, targetColumnId: number) => {
      setData((prevBoard) => {
        const newBoard = JSON.parse(JSON.stringify(prevBoard))

        // Variáveis para armazenar o card encontrado e seu índice na coluna
        let card: DefaultCardType | InvestimentCardType | undefined
        let sourceCardIndex = -1

        // Encontra a coluna de origem do card
        const sourceColumn = newBoard.find((column: any) => {
          sourceCardIndex = column.cardList.findIndex(
            (c: { id: string }) => c.id === cardId
          )
          if (sourceCardIndex !== -1) {
            card = column.cardList[sourceCardIndex]
            return true
          }
          return false
        })

        // Caso o card ou a coluna de origem não sejam encontrados, retorna o estado anterior
        if (!sourceColumn || !card) {
          return prevBoard
        }

        // Encontra a coluna de destino
        const targetColumn = newBoard.find(
          (col: any) => col.id === targetColumnId
        )
        // Se a coluna de destino não for encontrada, retorna o estado anterior
        if (!targetColumn) {
          return prevBoard
        }

        // Verifica se a movimentação é permitida com base na lista de colunas móveis
        const isMovable = Array.isArray(sourceColumn.moveable)
          ? sourceColumn.moveable.includes(targetColumnId)
          : sourceColumn.moveable !== false

        if (!isMovable) {
          return prevBoard
        }

        onDropCard && onDropCard(card, targetColumnId)

        // Remove o card da coluna de origem
        sourceColumn.cardList.splice(sourceCardIndex, 1)

        // Cria novo card com updatedAt atualizado
        const updatedCard = {
          ...card,
          columnId: targetColumnId,
          updatedAt: new Date().toISOString(),
        }

        // Adiciona e ordena os cards na coluna de destino
        targetColumn.cardList.push(updatedCard)
        // Ordena a lista com base no updatedAt após a inserção
        targetColumn.cardList.sort(
          (a: any, b: any) =>
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        )

        return newBoard
      })
    },
    [setData]
  )

  return (
    <BoardContext.Provider value={{ data, moveCard }}>
      {children}
    </BoardContext.Provider>
  )
}

const useBoard = () => useContext(BoardContext)

export { BoardProvider, useBoard }
